import { useRollbar } from '@rollbar/react'
import { mergeWith } from 'lodash'
import { useCallback, useEffect, useState } from 'react'

import envConfig from '~/configs/env'
import type { PreferredLanguage } from '~/data/types/preferredUnits'
import { useBootMark } from '~/debug/bootProfiler'
import useLogger from '~/debug/logger'
import type { I18nConfig, TranslationMessages } from '~/i18n/types'
import { useSnackbarControls } from '~/store/slices/display'

const MAX_RETRIES = 3

// Tagging the URL with the build SHA forces a fresh fetch on every deploy:
// the cached entry under the previous SHA's URL becomes unreachable and the
// new SHA's URL is uncached. Within a single deploy the URL is stable, and
// next.config.js serves /locales/* as `immutable`, so warm boots hit the
// browser cache forever with no revalidation. When appVersion is unset (e.g.
// local dev, where files are served fresh anyway) the URL is left unversioned.
const cacheBust = envConfig.appVersion ? `?v=${encodeURIComponent(envConfig.appVersion)}` : ''

export default function useTranslationMessages(
  preferredLanguage: PreferredLanguage | undefined,
  i18nConfig: I18nConfig
) {
  const logger = useLogger('useTranslationMessages')
  const rollbar = useRollbar()
  const { openSnackbar } = useSnackbarControls()
  const [translationMessages, setTranslationMessages] = useState<TranslationMessages>()
  useBootMark({ phase: 'translations-resolved', when: !!translationMessages })

  // TODO: delete this after i18n translation QA is complete
  const applyConfig = useCallback(
    (messages: TranslationMessages) => {
      return mergeWith({}, messages, (_, sourceValue) => {
        if (typeof sourceValue === 'string') {
          if (i18nConfig.blank) {
            return ''
          } else if (i18nConfig.randomize) {
            return generateRandomString()
          }
        }
      }) as TranslationMessages
    },
    [i18nConfig.blank, i18nConfig.randomize]
  )

  // Effect to fetch translation messages based on preferred language
  useEffect(() => {
    // If preferred language is not determined yet, don't fetch translations
    if (!preferredLanguage) return

    // Cancellation guard: when preferredLanguage changes (cached → /me, or
    // user changes language), this effect re-runs. The previous run may
    // still be in-flight, possibly serializing retries with 1s delays. If
    // it resolves after the new run, its setState would overwrite the
    // correct language. Mark stale runs cancelled before they touch state.
    let cancelled = false

    const fetchWithRetry = async (language: PreferredLanguage) => {
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        const warn = (message: unknown) =>
          logger.warn(`Attempt ${attempt}/${MAX_RETRIES} failed for ${language}.json: ${message}`)

        try {
          const response = await fetch(`/locales/${language}.json${cacheBust}`)
          if (cancelled) return undefined

          if (response.ok) {
            logger.info(`Successfully fetched ${language}.json`)
            return (await response.json()) as TranslationMessages
          }

          warn(`${response.status} ${response.statusText}`)
        } catch (error) {
          warn(error)
        }

        // Add a small delay between retries (except for the last attempt)
        if (attempt < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, 1000))
          if (cancelled) return undefined
        }
      }
    }

    const fetchTranslations = async () => {
      // First, try to fetch the preferred language
      let messages = await fetchWithRetry(preferredLanguage)
      if (cancelled) return

      // If preferred language fails and it's not English, try English as fallback
      if (!messages) {
        const prefersEnglish = preferredLanguage === 'en'
        if (!prefersEnglish) {
          logger.warn(`Failed to fetch ${preferredLanguage}.json after 3 attempts, falling back to English`)
          messages = await fetchWithRetry('en')
          if (cancelled) return
        }

        if (messages) {
          openSnackbar('We were unable to load translations for your preferred language.', { severity: 'warning' })
        } else {
          // We don't have any translation file at all, so the app won't render.
          openSnackbar('Something went wrong. Please try again or contact support.', { severity: 'error' })
        }
      }

      if (messages) {
        setTranslationMessages(applyConfig(messages))
      } else {
        const message = 'Failed to fetch any translation files, including English fallback'
        logger.error(message)

        if (process.env.NODE_ENV === 'production') {
          rollbar.error(message)
        }
      }
    }

    fetchTranslations()
    return () => {
      cancelled = true
    }
  }, [preferredLanguage, logger, rollbar, openSnackbar, applyConfig])

  return translationMessages
}

function generateRandomString() {
  const minLength = 5
  const maxLength = 15
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 '
  const length = Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength
  return Array.from({ length }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('')
}
